import React, { useEffect, useState } from 'react';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import TrainingsService from '../../services/trainings.service';
import useService from '../../hooks/service.hook';
import { format } from 'date-fns';
import { palette } from '../../theme';

interface TransferWorkoutModalProps {
  open: boolean;
  onClose: () => void;
  trainingId: number;
  trainingTime: string;
  trainingTitle: string;
}

interface TransferOption {
  id: number;
  title: string;
  time: string;
}

const TransferWorkoutModal: React.FC<TransferWorkoutModalProps> = ({
  open,
  onClose,
  trainingId,
  trainingTime,
  trainingTitle,
}) => {
  const [plannedTraining, setPlannedTraining] = useState('');
  const [options, setOptions] = useState<TransferOption[]>([]);
  const [loading, setLoading] = useState(false);
  const trainingsService = useService(TrainingsService);

  useEffect(() => {
    if (!open) return;
    setPlannedTraining('');
    setOptions([]);
    setLoading(true);
    trainingsService
      .getTransferTrainingOptions(trainingTime)
      .then((res: any) => {
        setOptions(res.data.data || []);
        setLoading(false);
      })
      .catch(() => {
        setOptions([]);
        setLoading(false);
      });
  }, [open, trainingTime]);

  const handleWorkoutTransfer = () => {
    console.log('handleWorkoutTransfer');
    setLoading(true);
    trainingsService
      .transferWorkoutData(plannedTraining, trainingId.toString())
      .then(() => {
        setLoading(false);
        onClose();
      })
      .catch(() => {
        onClose();
      });
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Paper
        sx={{
          width: '500px',
          maxWidth: '100vw',
          padding: '32px 24px',
          boxSizing: 'border-box',
          zIndex: 2000,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={onClose}>
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h5"
            sx={{
              flexGrow: 1,
              textAlign: 'center',
              fontWeight: 700,
              color: palette.secondary.main,
              letterSpacing: 0.5,
            }}
          >
            Transfer Workout
          </Typography>
          <Box sx={{ width: 48 }} /> {/* Spacer for symmetry */}
        </Box>
        <Typography variant="h5" sx={{ textAlign: 'center', mb: 4, color: palette.secondary.main }}>
          {trainingTitle}
        </Typography>
        <Typography variant="subtitle2" sx={{ mb: 1, color: palette.secondary.main }}>
          Attach To Other Planned Training
        </Typography>
        <FormControl fullWidth sx={{ mb: 6 }}>
          <Select
            displayEmpty
            value={plannedTraining}
            onChange={(e) => setPlannedTraining(e.target.value as string)}
            renderValue={(selected) =>
              selected ? options.find((o) => o.id === Number(selected))?.title : 'Select Planned Training'
            }
            sx={{ background: palette.grey[100] }}
            disabled={loading || options.length === 0}
          >
            <MenuItem value="" disabled>
              Select Planned Training
            </MenuItem>
            {options.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                <Box>
                  <Typography variant="subtitle2">{option.title}</Typography>
                  <Typography variant="caption" sx={{ color: 'gray' }}>
                    {format(new Date(option.time), 'PPpp')}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
          {loading && (
            <Typography variant="body1" sx={{ mt: 2, color: 'gray' }}>
              Loading options...
            </Typography>
          )}
          {!loading && options.length === 0 && (
            <Typography variant="body1" sx={{ mt: 2, color: 'gray' }}>
              No planned trainings available.
            </Typography>
          )}
        </FormControl>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          sx={{ mt: 8, height: 56, fontSize: 18, fontWeight: 600 }}
          disabled={!plannedTraining}
          onClick={handleWorkoutTransfer}
        >
          Transfer
        </Button>
      </Paper>
    </Modal>
  );
};

export default TransferWorkoutModal;
